import React, { useEffect, useRef } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  Image,
  SimpleGrid,
  Link,
  useColorModeValue,
} from "@chakra-ui/react";
import { GreshamHeader } from "../components/GreshamHeader";
import { GreshamFooter } from "../components/GreshamFooter";

const PrivacyPolicy: React.FC = () => {
  const textColor = useColorModeValue("gray.800", "white");
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current) {
        const scrolled = window.pageYOffset;
        const rate = scrolled * -0.5;
        parallaxRef.current.style.transform = `translate3d(0, ${rate}px, 0)`;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box
      minHeight="100vh"
      bg="white"
      css={{
        overscrollBehavior: "none",
        "-webkit-overflow-scrolling": "touch"
      }}
    >
      <GreshamHeader />

      {/* Hero Section with Background Image */}
      <Box
        as="section"
        w="full"
        position="relative"
        h="100vh"
        minH="600px"
        overflow="hidden"
        clipPath="inset(0)"
      >
        {/* Background Image Container with Parallax */}
        <Box
          ref={parallaxRef}
          position="fixed"
          top="0"
          left="0"
          right="0"
          height="120vh"
          backgroundImage="url('/background/polina-kuzovkova-sar-Y4BxZDA-unsplash.jpg')"
          backgroundSize="cover"
          backgroundPosition="center center"
          backgroundRepeat="no-repeat"
          filter="brightness(0.7)"
          willChange="transform"
        />

        {/* Text Container - Overlay on background for mobile, positioned for desktop */}
        <Box
          position={{ base: "absolute", lg: "absolute" }}
          top={{ base: "50%", lg: 0 }}
          left={{ base: 0, lg: 0 }}
          w={{ base: "full", lg: "70%" }}
          h={{ base: "auto", lg: "calc(120px + 300px)", xl: "calc(140px + 300px)", "2xl": "calc(160px + 300px)" }}
          display="flex"
          alignItems={{ base: "center", lg: "flex-end" }}
          justifyContent={{ base: "center", lg: "flex-start" }}
          zIndex={100}
          bg={{ base: "transparent", lg: "transparent" }}
          mb={{ base: 0, lg: 0 }}
          order={{ base: 1, lg: 0 }}
          pt={{ base: 0, md: 0, lg: "120px", xl: "140px", "2xl": "160px" }}
          pb={{ base: 0, md: 0, lg: 0 }}
          px={{ base: 6, sm: 8, md: 12, lg: 16 }}
          transform={{ base: "translateY(-50%)", lg: "none" }}
        >
          <VStack spacing={3} align="flex-start" maxW={{ base: "90%", sm: "80%", md: "700px", lg: "800px" }} ml={{ base: "auto", lg: "10%" }} mr="auto" textAlign="left" justify="center">
            {/* Space placeholder like homepage */}
            <Text
              fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
              color="white"
              fontFamily="'Carmen Sans Regular', sans-serif"
              fontWeight="300"
              letterSpacing="0.05em"
              className="slide-in-left"
            >
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </Text>

            {/* Main Heading */}
            <Heading
              as="h1"
              fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
              fontWeight="bold"
              color="white"
              fontFamily="'Carmen Sans Bold', sans-serif"
              lineHeight="1.15"
              maxW={{ base: "100%", md: "900px", lg: "1000px" }}
              className="slide-in-left-delay-1"
            >
              Privacy Policy
            </Heading>
          </VStack>
        </Box>
      </Box>

      {/* Privacy Policy Content Section */}
      <Box
        as="section"
        w="full"
        py={{ base: 16, md: 20 }}
        px={{ base: 8, md: 16, lg: 20 }}
        bg="white"
        mt="-80px"
        position="relative"
        zIndex={20}
        borderRadius="3xl 3xl 0 0"
        boxShadow="0 -20px 40px rgba(0,0,0,0.05)"
      >
        <Box
          px={{ base: 6, md: 12, lg: 16 }}
          w="full"
        >
          {/* Introduction Content without heading */}
          <Box mb={12}>
            <Text
              fontSize={{ base: "sm", md: "md" }}
              color={textColor}
              fontFamily="'Carmen Sans Light', sans-serif"
              lineHeight="1.6"
              fontWeight="300"
              opacity={0.8}
            >
              Mercurian Equity Pty Ltd and our related bodies (referred to in this document as 'we', 'us' or 'our') are committed to managing your personal information in accordance with the Australian Privacy Principles under the Privacy Act{" "}
              1988
              {" "}(Cth) and in accordance with other applicable privacy laws. This document sets out our policies for managing your personal information and is referred to as our Privacy Policy. This Privacy Policy explains how we collect, hold, use, disclose and protect personal information.
            </Text>
          </Box>

          {/* Personal Information Section */}
          <Box mb={24}>
            <Heading
              as="h2"
              fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
              fontWeight="bold"
              color={textColor}
              fontFamily="'Carmen Sans Bold', sans-serif"
              lineHeight="1.2"
              letterSpacing="0.01em"
              mb={8}
            >
              Personal Information Mercurian Equity collects
            </Heading>
            <VStack align="stretch" spacing={4}>
              <Text
                fontSize={{ base: "sm", md: "md" }}
                color={textColor}
                fontFamily="'Carmen Sans Light', sans-serif"
                lineHeight="1.6"
                fontWeight="300"
                opacity={0.8}
              >
                The type of personal information that we collect will vary depending on the circumstances of collection and the kind of service that you request from us, but will typically include:
              </Text>
              
              <Box display="flex" alignItems="flex-start">
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  mr={2}
                  opacity={0.8}
                >
                  •
                </Text>
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                  opacity={0.8}
                >
                  Name;
                </Text>
              </Box>
              
              <Box display="flex" alignItems="flex-start">
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  mr={2}
                  opacity={0.8}
                >
                  •
                </Text>
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                  opacity={0.8}
                >
                  Contact details;
                </Text>
              </Box>
              
              <Box display="flex" alignItems="flex-start">
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  mr={2}
                  opacity={0.8}
                >
                  •
                </Text>
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                  opacity={0.8}
                >
                  any additional personal information you provide to us, or authorise us to collect, as part of your interaction with us.
                </Text>
              </Box>
            </VStack>
          </Box>

          {/* Personal Information Usage Section with Image */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 8, lg: 12 }} alignItems="flex-start">
            {/* Left Column - Ocean Wave Image */}
            <Box>
              <Image
                src="/background/yuliya-matuzava-dGYW97m0voU-unsplash.jpg"
                alt="Modern architecture"
                w="full"
                h={{ base: "500px", md: "600px", lg: "700px" }}
                objectFit="cover"
                borderRadius="md"
              />
            </Box>

            {/* Right Column - Text Content */}
            <Box>
              <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                fontWeight="bold"
                color={textColor}
                fontFamily="'Carmen Sans Bold', sans-serif"
                lineHeight="1.2"
                letterSpacing="0.01em"
                mb={6}
              >
                How do we use and disclose your personal information?
              </Heading>
              
              <VStack align="stretch" spacing={4}>
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                  opacity={0.8}
                >
                  We will collect personal information as reasonably necessary for us to carry out our business and to provide our products and services. We also use your personal information to comply with Australian taxation laws, Anti-Money Laundering and Counter-Terrorism laws and all other applicable laws; and discloses personal information to various law enforcement agencies, regulatory authorities and governments.
                </Text>
                
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                  opacity={0.8}
                >
                  Additionally, your personal information may also be used for secondary purposes such as for the provision of promotional and marketing material. You can notify us at any time if you do not wish to receive such materials or information. Where we collect your personal information for a specific purpose not contemplated in this Privacy Policy, we will provide you with a collection notice which explains the primary purpose and any related secondary purposes for which your personal information is being collected.
                </Text>
                
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                  opacity={0.8}
                >
                  We may collect information based on how you use our website (www.mercurianequity.com and www.mercurianequity.com.au). We use "cookies" and other data collection methods to collect information on website activity, such as the number of visitors and the number of pages viewed. This information can be collected to analyse and improve our website and to record statistics on web traffic. We do not use this information to personally identify you.
                </Text>
                
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                  opacity={0.8}
                >
                  We do not actively seek to collect sensitive information (for example health information or information about any criminal record), unless it is necessary for our business purposes. If we do have to collect sensitive information, we will only collect, use and disclose it in accordance with privacy laws.
                </Text>
              </VStack>
            </Box>
          </SimpleGrid>
          
          {/* The Investment Process Section */}
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 8, lg: 12 }} alignItems="center" mt={56}>
            {/* Left Column - Text Content */}
            <Box>
              <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                fontWeight="bold"
                color={textColor}
                fontFamily="'Carmen Sans Bold', sans-serif"
                lineHeight="1.2"
                letterSpacing="0.01em"
                mb={6}
              >
                How do we store your personal information?
              </Heading>
              
              <VStack align="stretch" spacing={4}>
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                  opacity={0.8}
                >
                  We aim to take all reasonable steps to protect your personal information from misuse, loss, unauthorised access, interference, modification or disclosure.
                </Text>
                
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                  opacity={0.8}
                >
                  We have physical, electronic and procedural safeguards to protect your information which is held by us. For example, your personal information is stored in secured offices and access to it is protected by a range of security measures such as secure authentication, password controls, encryption, firewalls and anti-virus technology. Access to personal information is also restricted to staff whose job purpose requires access to it and we also require all employees to maintain strict confidentiality at all times regarding the personal information of our investors.
                </Text>
                
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color={textColor}
                  fontFamily="'Carmen Sans Light', sans-serif"
                  lineHeight="1.6"
                  fontWeight="300"
                  opacity={0.8}
                >
                  Where the personal information we collect is no longer required, we use secure methods to permanently destroy or de-identify any personal information in accordance with this privacy policy and all applicable laws.
                </Text>
              </VStack>
            </Box>

            {/* Right Column - Classical Architecture Image */}
            <Box>
              <Image
                src="/background/giorgio-trovato-VavXiCDrOgA-unsplash.jpg"
                alt="Classical architecture"
                w="full"
                h={{ base: "500px", md: "600px", lg: "700px" }}
                objectFit="cover"
                borderRadius="md"
              />
            </Box>
          </SimpleGrid>
          
          {/* Accessing and updating personal information Section */}
          <Box mt={40}>
            <Heading
              as="h2"
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="bold"
              color={textColor}
              fontFamily="'Carmen Sans Bold', sans-serif"
              lineHeight="1.2"
              letterSpacing="0.01em"
              mb={6}
            >
              Accessing and updating your personal information
            </Heading>
            <VStack align="stretch" spacing={4}>
              <Text
                fontSize={{ base: "sm", md: "md" }}
                color={textColor}
                fontFamily="'Carmen Sans Light', sans-serif"
                lineHeight="1.6"
                fontWeight="300"
                opacity={0.8}
              >
                If you require access to or wish to update your personal information, please contact us:
              </Text>
              
              <Text
                fontSize={{ base: "sm", md: "md" }}
                color={textColor}
                fontFamily="'Carmen Sans Light', sans-serif"
                lineHeight="1.6"
                fontWeight="300"
                opacity={0.8}
              >
                By e-mail: client@mercurianequity.com
              </Text>
              
              <Text
                fontSize={{ base: "sm", md: "md" }}
                color={textColor}
                fontFamily="'Carmen Sans Light', sans-serif"
                lineHeight="1.6"
                fontWeight="300"
                opacity={0.8}
              >
                By post: Level 28, 140 St Georges Terrace Perth, Western Australia, 6000
              </Text>
              
              <Text
                fontSize={{ base: "sm", md: "md" }}
                color={textColor}
                fontFamily="'Carmen Sans Light', sans-serif"
                lineHeight="1.6"
                fontWeight="300"
                opacity={0.8}
              >
                If you believe that the personal information we hold about you is inaccurate, incomplete or out of date, you should contact us to correct this information. We aim to promptly update any personal information that is inaccurate, incomplete or out of date. If we refuse to correct your personal information, we will provide you with a written notice that sets out the reasons for our refusal (unless it would be unreasonable to provide those reasons) and provide you with a statement on what you can do if you are not satisfied with our response.
              </Text>
            </VStack>
          </Box>
          
          {/* Additional Privacy Policy Sections */}
          <Box mt={24}>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 8, lg: 12 }}>
              {/* Left Column - Complaints and Australian Privacy Commissioner */}
              <VStack align="stretch" spacing={8}>
                {/* Complaints and feedback Section */}
                <Box>
                  <Heading
                    as="h3"
                    fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                    fontWeight="bold"
                    color={textColor}
                    fontFamily="'Carmen Sans Bold', sans-serif"
                    lineHeight="1.2"
                    letterSpacing="0.01em"
                    mb={6}
                  >
                    Complaints and feedback
                  </Heading>
                  <VStack align="stretch" spacing={4}>
                    <Text
                      fontSize={{ base: "sm", md: "md" }}
                      color={textColor}
                      fontFamily="'Carmen Sans Light', sans-serif"
                      lineHeight="1.6"
                      fontWeight="300"
                      opacity={0.8}
                    >
                      If you wish to make a complaint about a breach of the Privacy Act, Australian Privacy Principles or a privacy code that applies to us, please contact us as set out below and we will take reasonable steps to investigate the complaint and respond to you.
                    </Text>
                    
                    <Text
                      fontSize={{ base: "sm", md: "md" }}
                      color={textColor}
                      fontFamily="'Carmen Sans Light', sans-serif"
                      lineHeight="1.6"
                      fontWeight="300"
                      opacity={0.8}
                    >
                      If you have any queries or concerns about our privacy policy or the way we handle your personal information, please contact us:
                    </Text>
                    
                    <Text
                      fontSize={{ base: "sm", md: "md" }}
                      color={textColor}
                      fontFamily="'Carmen Sans Light', sans-serif"
                      lineHeight="1.6"
                      fontWeight="300"
                      opacity={0.8}
                    >
                      By e-mail: client@mercurianequity.com
                    </Text>
                    
                    <Text
                      fontSize={{ base: "sm", md: "md" }}
                      color={textColor}
                      fontFamily="'Carmen Sans Light', sans-serif"
                      lineHeight="1.6"
                      fontWeight="300"
                      opacity={0.8}
                    >
                      By post: Level 28, 140 St Georges Terrace Perth, Western Australia, 6000
                    </Text>
                  </VStack>
                </Box>

                {/* Australian Privacy Commissioner Section */}
                <Box>
                  <Heading
                    as="h3"
                    fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                    fontWeight="bold"
                    color={textColor}
                    fontFamily="'Carmen Sans Bold', sans-serif"
                    lineHeight="1.2"
                    letterSpacing="0.01em"
                    mb={6}
                  >
                    Australian Privacy Commissioner
                  </Heading>
                  <VStack align="stretch" spacing={4}>
                    <Text
                      fontSize={{ base: "sm", md: "md" }}
                      color={textColor}
                      fontFamily="'Carmen Sans Light', sans-serif"
                      lineHeight="1.6"
                      fontWeight="300"
                      opacity={0.8}
                    >
                      If you are not satisfied with our response, you may contact the Australian Privacy Commissioner:
                    </Text>
                    
                    <Text
                      fontSize={{ base: "sm", md: "md" }}
                      color={textColor}
                      fontFamily="'Carmen Sans Light', sans-serif"
                      lineHeight="1.6"
                      fontWeight="300"
                      opacity={0.8}
                    >
                      Telephone: 1300 363 992
                    </Text>
                    
                    <Text
                      fontSize={{ base: "sm", md: "md" }}
                      color={textColor}
                      fontFamily="'Carmen Sans Light', sans-serif"
                      lineHeight="1.6"
                      fontWeight="300"
                      opacity={0.8}
                    >
                      Email: enquiries@oaic.gov.au
                    </Text>
                  </VStack>
                </Box>
              </VStack>
              
              {/* Right Column - Changes to Policy and More Information */}
              <VStack align="stretch" spacing={8}>
                {/* Changes to this Privacy Policy Section */}
                <Box>
                  <Heading
                    as="h3"
                    fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                    fontWeight="bold"
                    color={textColor}
                    fontFamily="'Carmen Sans Bold', sans-serif"
                    lineHeight="1.2"
                    letterSpacing="0.01em"
                    mb={6}
                  >
                    Changes to this Privacy Policy
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    lineHeight="1.6"
                    fontWeight="300"
                    opacity={0.8}
                  >
                    This Privacy Policy is dated 1 July 2024. This document replaces any other Privacy Policy published by us. We may make changes to this Policy from time to time. We encourage you to review our website regularly to ensure that you are reviewing the most current version of our Privacy Policy.
                  </Text>
                </Box>

                {/* More information Section */}
                <Box>
                  <Heading
                    as="h3"
                    fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                    fontWeight="bold"
                    color={textColor}
                    fontFamily="'Carmen Sans Bold', sans-serif"
                    lineHeight="1.2"
                    letterSpacing="0.01em"
                    mb={6}
                  >
                    More information
                  </Heading>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    fontFamily="'Carmen Sans Light', sans-serif"
                    lineHeight="1.6"
                    fontWeight="300"
                    opacity={0.8}
                  >
                    For more information about privacy in general, you can visit the Office of the Australian Information Commissioner's website at{" "}
                    <Link
                      href="https://www.oaic.gov.au"
                      target="_blank"
                      rel="noopener noreferrer"
                      color="blue.600"
                      textDecoration="underline"
                      _hover={{ color: "blue.800" }}
                    >
                      www.oaic.gov.au
                    </Link>
                    .
                  </Text>
                </Box>
              </VStack>
            </SimpleGrid>
          </Box>
        </Box>
      </Box>

      <GreshamFooter />
    </Box>
  );
};

export default PrivacyPolicy;